import React from 'react';
import { Loader2, CheckCircle2, AlertCircle, Scale } from 'lucide-react';
import { API_BASE } from '../services/api';

interface Props {
 estateId: string;
 onUnlocked: (estateId: string) => void;
 onCancel: () => void;
}

// Poll every 2 seconds for up to ~30 seconds before surfacing the"still
// confirming" fallback. Most webhooks arrive in under 5s; the longer budget
// covers Stripe retry delays without making the user sit on a spinner forever.
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 15;

type Status = 'polling' | 'unlocked' | 'timeout' | 'unauthenticated';

export const UnlockingPage: React.FC<Props> = ({ estateId, onUnlocked, onCancel }) => {
 const [status, setStatus] = React.useState<Status>('polling');

 React.useEffect(() => {
 let cancelled = false;
 let timer: ReturnType<typeof setTimeout> | null = null;
 let polls = 0;

 const poll = async () => {
 if (cancelled) return;
 try {
 const r = await fetch(`${API_BASE}/api/estates/${estateId}`, { credentials: 'include' });
 if (r.status === 401 || r.status === 403) {
 // Session genuinely missing — nothing we can recover from
 // by polling. Send the user to sign in.
 setStatus('unauthenticated');
 return;
 }
 if (r.ok) {
 const data = await r.json();
 if (data?.status === 'unlocked') {
 setStatus('unlocked');
 // Short confirmation delay before handing off to the app.
 setTimeout(() => {
 if (!cancelled) onUnlocked(estateId);
 }, 700);
 return;
 }
 }
 } catch {
 // Network blip — fall through and retry.
 }

 polls += 1;
 if (polls >= MAX_POLLS) {
 setStatus('timeout');
 return;
 }
 timer = setTimeout(poll, POLL_INTERVAL_MS);
 };

 poll();

 return () => {
 cancelled = true;
 if (timer) clearTimeout(timer);
 };
 }, [estateId, onUnlocked]);

 return (
 <div className="min-h-screen flex items-center justify-center px-6">
 <div className="max-w-md w-full bg-white rounded border border-[#E8E4DB] p-8 text-center">
 <div className="flex items-center justify-center gap-2 mb-6 text-[#6B6762]">
 <Scale className="w-4 h-4 text-[#3730A3]" />
 <span className="font-semibold text-sm">EstateClarity</span>
 </div>

 {status === 'polling' && (
 <>
 <div className="w-14 h-14 rounded-full bg-[#EEF0FA] flex items-center justify-center mx-auto mb-4">
 <Loader2 className="w-7 h-7 text-[#3730A3] animate-spin" />
 </div>
 <h1 className="text-xl font-bold text-[#1A1A1A] mb-2">
 Unlocking your report
 </h1>
 <p className="text-sm text-[#3F3D39] leading-relaxed">
 Payment received — we're confirming access and preparing your
 full estate report. This usually takes just a few seconds.
 </p>
 </>
 )}

 {status === 'unlocked' && (
 <>
 <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
 <CheckCircle2 className="w-7 h-7 text-[#3730A3]" />
 </div>
 <h1 className="text-xl font-bold text-[#1A1A1A] mb-2">
 Report unlocked
 </h1>
 <p className="text-sm text-[#3F3D39] leading-relaxed">
 Opening your full report now…
 </p>
 </>
 )}

 {status === 'timeout' && (
 <>
 <div className="w-14 h-14 rounded-full bg-[#FAF3E0] flex items-center justify-center mx-auto mb-4">
 <AlertCircle className="w-7 h-7 text-[#7A5A00]" />
 </div>
 <h1 className="text-xl font-bold text-[#1A1A1A] mb-2">
 Still confirming your payment
 </h1>
 <p className="text-sm text-[#3F3D39] leading-relaxed mb-5">
 Payment usually confirms in a few seconds. Your report will unlock
 automatically as soon as Stripe notifies us — you can open your
 estate now and it will update when confirmation arrives.
 </p>
 <button
 onClick={() => onUnlocked(estateId)}
 className="w-full bg-[#3730A3] hover:bg-[#2E2780] text-white font-medium px-4 py-2.5 rounded-lg transition-colors"
 >
 Open my estate
 </button>
 <button
 onClick={onCancel}
 className="mt-2 w-full text-sm text-[#6B6762] hover:text-[#3F3D39] px-4 py-2 transition-colors"
 >
 Go to home
 </button>
 </>
 )}

 {status === 'unauthenticated' && (
 <>
 <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
 <AlertCircle className="w-7 h-7 text-[#1A1A1A]" />
 </div>
 <h1 className="text-xl font-bold text-[#1A1A1A] mb-2">
 Please sign in to view your report
 </h1>
 <p className="text-sm text-[#3F3D39] leading-relaxed mb-5">
 Your payment was received. Sign in with the email you used
 at checkout and your unlocked report will be waiting.
 </p>
 <button
 onClick={onCancel}
 className="w-full bg-[#3730A3] hover:bg-[#2E2780] text-white font-medium px-4 py-2.5 rounded-lg transition-colors"
 >
 Go to sign in
 </button>
 </>
 )}
 </div>
 </div>
 );
};
